import { z } from 'zod'

// --- Product (stored in config.products) ---
export interface Product {
  name: string
  sku: string
  points: number
}

// --- Category thresholds ---
export interface CategoryValues {
  oro: number
  marmol: number
  bronce: number
}

// --- Config (API response) ---
export interface Config {
  id: number
  id_company: number
  min_exchange_price: number
  min_exchange_points: number
  value_point_to_CLP: number
  value_CLP_to_point: number
  months_expiration_points: number
  products: Product[]
  categories: {
    type: string
    values: CategoryValues
  }
  list_notification_emails: Record<string, string>
  email_flags: Record<string, boolean>
}

// --- Exchange settings form schema ---
export const exchangeSettingsSchema = z.object({
  min_exchange_points: z.number({ required_error: 'Requerido' }).min(0, 'Debe ser mayor o igual a 0'),
  value_point_to_CLP: z.number({ required_error: 'Requerido' }).min(0, 'Debe ser mayor o igual a 0'),
  value_CLP_to_point: z.number({ required_error: 'Requerido' }).min(0, 'Debe ser mayor o igual a 0'),
  months_expiration_points: z.number({ required_error: 'Requerido' }).min(1, 'Debe ser al menos 1'),
})

export type ExchangeSettingsFormData = z.infer<typeof exchangeSettingsSchema>

// --- Categories form schema ---
export const categoriesSchema = z.object({
  oro: z.number({ required_error: 'Requerido' }).min(0, 'Debe ser mayor o igual a 0'),
  marmol: z.number({ required_error: 'Requerido' }).min(0, 'Debe ser mayor o igual a 0'),
  bronce: z.number({ required_error: 'Requerido' }).min(0, 'Debe ser mayor o igual a 0'),
})

export type CategoriesFormData = z.infer<typeof categoriesSchema>

// --- Product form schema ---
export const productFormSchema = z.object({
  name: z.string().min(1, 'Nombre es requerido'),
  sku: z.string().min(1, 'SKU es requerido'),
  points: z.number({ required_error: 'Puntos es requerido' }).min(0, 'Debe ser mayor o igual a 0'),
})

export type ProductFormData = z.infer<typeof productFormSchema>

// --- Process Daily (CRON) response ---
// The daily benefits job runs as a BackgroundTask. The POST handler returns
// `ProcessDailyStartedResponse` immediately; the counters land in
// `cron_execution_logs.result` and must be polled via `GET /cron_logs/{id}`.
export interface ProcessDailyResponse {
  birthdays_processed: number
  birthdays_benefits_granted: number
  recurring_processed: number
  recurring_benefits_granted: number
  expired_benefits: number
  expired_vouchers: number
  expiring_notifications_sent: number
  category_changes: number
  category_upgrades: number
  category_downgrades: number
  benefits_cancelled_downgrade: number
  benefits_cancelled_upgrade: number
  errors: string[]
}

export interface ProcessDailyStartedResponse {
  id_execution: number
  status: 'started'
}

export type CronLogStatus = 'started' | 'success' | 'partial' | 'error'

export interface CronLogResponse {
  id: number
  started_at: string
  finished_at: string
  duration_seconds: number
  status: CronLogStatus
  result: ProcessDailyResponse | null
  error_message: string | null
  trigger_source: string
}
